<script setup lang="ts">
import type { DungeonEnvironment, DungeonRun, DungeonSeason } from '~/composables/charon'

const { charon } = useCharon()
const { t } = useI18n()

const props = defineProps<{
  leaderboardSize: number
  initialSeason: number
  seasons: DungeonSeason[]
  environments: DungeonEnvironment[]
}>()

const endlessPage = ref(0)
const endlessSeason = ref(props.initialSeason || props.seasons[0].number)
const endlessEnvironment = ref(props.environments[0].identifier)
const endlessPlayers = ref(1)

const { data: leaderboard } = useAsyncData(
  () => charon<DungeonRun[]>(
    `/dungeons/leaderboards/endless`,
    {
      params: {
        'season[eq]': endlessSeason.value,
        'players[size]': endlessPlayers.value,
        'environment[eq]': endlessEnvironment.value,
        'sort': ['finishedLevel[dec]', 'duration[asc]'],
        'page': endlessPage.value,
        'size': props.leaderboardSize,
      },
    },
  ),
  {
    watch: [endlessSeason, endlessPlayers, endlessEnvironment, endlessPage],
    lazy: false,
    server: true,
    transform: vs => vs?.map((v, i) => ({ ...v, place: i + 1 + endlessPage.value * props.leaderboardSize })) ?? null,
  },
)

const { data: maxPage } = useAsyncData(
  () => charon<number>(
    `/dungeons/leaderboards/endless/count`,
    {
      params: {
        'season[eq]': endlessSeason.value,
        'players[size]': endlessPlayers.value,
        'environment[eq]': endlessEnvironment.value,
      },
    },
  ),
  {
    watch: [endlessSeason, endlessPlayers, endlessEnvironment],
    transform: count => Math.ceil(count / props.leaderboardSize),
    lazy: false,
    server: true,
  },
)

// reset page on filter update
watch([endlessSeason, endlessPlayers, endlessEnvironment], () => endlessPage.value = 0)
</script>

<template>
  <DungeonRunLeaderboard
    v-model:page="endlessPage"
    :runs="leaderboard"
    :max-page="maxPage ?? 0"
    :size="leaderboardSize"
  >
    <div class="flex flex-col tablet:flex-row gap-2 justify-between">
      <select
        v-model="endlessSeason"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="season in props.seasons"
          :key="season.id"
        >
          <option
            :value="season.number"
          >
            {{ t(`jc.view.dungeon.leaderboard.season.label`, { season: season.number }) }}
          </option>
        </template>
      </select>
      <select
        v-model="endlessEnvironment"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="environment in props.environments"
          :key="environment.identifier"
        >
          <option
            :value="environment.identifier"
          >
            {{ t(`jc.dungeon.environment.${environment.identifier}.title`) }}
          </option>
        </template>
      </select>
      <select
        v-model="endlessPlayers"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="player in [1, 2, 3, 4]"
          :key="player"
        >
          <option
            :value="player"
          >
            {{ t(`jc.view.dungeon.leaderboard.players.label`, { players: player }) }}
          </option>
        </template>
      </select>
    </div>
  </DungeonRunLeaderboard>
</template>
